import { InboxOutlined, PaperClipOutlined } from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  Modal,
  Progress,
  Radio,
  Row,
  Space,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import Papa from 'papaparse';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useUploadFile } from '../../api/Transcripts';
import { BluePrimary } from '../../styles';
import { transcriptDetailsType } from '../../types/index';

const { TextArea } = Input;
const { Title, Paragraph, Text, Link } = Typography;
const { Dragger } = Upload;

const StyledInboxOutlined = styled(InboxOutlined)`
  display: block;
  color: ${BluePrimary};
  font-size: 48px;
  margin-bottom: 1rem;
`;

const StyledText = styled(Text)`
  color: ${BluePrimary};
  font-size: 13px;
`;

const StyledUploadText = styled(Text)`
  font-size: 15px;
  font-weight: 600;
`;

const StyledListItem = styled.li`
  list-style-type: disc;
`;

const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

function FileUploadForm({
  setTranscriptData,
  next,
  transcriptData,
  setCodingGuidanceSelected,
  finishUpload,
}) {
  const [interviewGuideFile, setInterviewGuideFile] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // Upload progress state
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);

  const uploadFile = useUploadFile();

  const normalizeToArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value) return [value];
    return [];
  };

  useEffect(() => {
    const details = transcriptData?.transcriptDetails;
    if (details) {
      const normalizedDetails = {
        ...details,
        interview_guide: normalizeToArray(details.interview_guide),
        transcripts: normalizeToArray(details.transcripts),
      };

      form.setFieldsValue(normalizedDetails);
      setInterviewGuideFile(normalizedDetails.interview_guide?.[0] || null);
    }
  }, [form, transcriptData]);

  const showConfirm = () => {
    Modal.confirm({
      title: 'Are you sure?',
      content: "This can't be undone. Your upload will not be processed by Fathom.",
      okText: 'Cancel upload',
      okButtonProps: { danger: true, type: 'primary' },
      cancelText: 'Continue with upload',
      onOk() {
        navigate('/');
      },
    });
  };

  const uploadFilesToGoogleDrive = async (files) => {
    const gdriveIds = [];
    const totalCount = files.length;
    setTotalFiles(totalCount);

    const uploadOne = async (fileItem, index) => {
      const file = fileItem.originFileObj || fileItem;
      setUploadStatus(`Uploading ${file.name}...`);
      try {
        const result = await uploadFile.mutateAsync({ file });
        gdriveIds.push(result.gdrive_file_id);
        setUploadProgress(((index + 1) / totalCount) * 100);
      } catch (err) {
        throw new Error(`Failed to upload ${file.name}: ${err.message}`);
      }
    };

    await files.reduce(
      (promise, fileItem, index) => promise.then(() => uploadOne(fileItem, index)),
      Promise.resolve(),
    );

    return gdriveIds;
  };

  const onFinish = async (formValues) => {
    setCodingGuidanceSelected(formValues.coding_guidance);

    try {
      // Show upload modal
      setUploadModalVisible(true);
      setUploadProgress(0);
      setUploadStatus('Starting upload...');

      // Upload interview guide first
      setUploadStatus('Uploading interview guide...');
      const guideFile =
        formValues.interview_guide[0].originFileObj || formValues.interview_guide[0];
      const guideResult = await uploadFile.mutateAsync({ file: guideFile });
      const guideGdriveId = guideResult.gdrive_file_id;

      // Upload all transcript files
      setUploadStatus('Uploading transcripts...');
      const transcriptGdriveIds = await uploadFilesToGoogleDrive(formValues.transcripts);

      // Parse the interview guide CSV to extract questions
      setUploadStatus('Processing interview guide...');
      const reader = new FileReader();

      reader.onload = async ({ target }) => {
        try {
          const csv = Papa.parse(target.result, { skipEmptyLines: true });
          const rows = csv.data;

          // Skip the first row only if it contains "question" or "questions" (header row)
          const firstRowIsHeader =
            rows.length > 0 && rows[0][0] && /^questions?$/i.test(rows[0][0].trim());
          const dataRows = firstRowIsHeader ? rows.slice(1) : rows;

          // Extract questions from the CSV
          const questions = dataRows
            .filter((row) => row[0]?.trim())
            .map((row, index) => ({
              index: index.toString(),
              text: row[0].trim(),
            }));

          if (questions.length === 0) {
            setUploadModalVisible(false);
            message.error({
              content: 'No questions found in the interview guide. Please check your CSV format.',
              duration: 5,
            });
            return;
          }

          // Calculate total OE responses for token calculation
          const totalOEResponses = questions.length * transcriptGdriveIds.length;

          const nextTranscriptData = {
            transcriptDetails: {
              ...formValues,
              interview_guide_gdrive_id: guideGdriveId,
              transcript_gdrive_ids: transcriptGdriveIds,
              total_oe_responses: totalOEResponses,
              // Persist these as arrays so Form + Upload can rehydrate cleanly
              interview_guide: formValues.interview_guide,
              transcripts: formValues.transcripts,
            },
            questions,
          };

          setTranscriptData(nextTranscriptData);

          setUploadModalVisible(false);

          if (formValues.coding_guidance) {
            next();
          } else {
            // If no coding guidance, go directly to finish upload
            finishUpload(nextTranscriptData);
          }
        } catch (err) {
          setUploadModalVisible(false);
          message.error('Something went wrong reading the interview guide CSV');
        }
      };

      reader.onerror = () => {
        setUploadModalVisible(false);
        message.error('Failed to read the interview guide file');
      };

      reader.readAsText(guideFile);
    } catch (err) {
      setUploadModalVisible(false);
      message.error(`File upload failed: ${err.message}`);
    }
  };

  return (
    <>
      <Modal
        open={uploadModalVisible}
        closable={false}
        footer={null}
        title="Uploading files to secure storage..."
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Progress percent={Math.round(uploadProgress)} status="active" />
          <Text>{uploadStatus}</Text>
          {totalFiles > 0 && (
            <Text type="secondary">
              {Math.round((uploadProgress / 100) * totalFiles)} of {totalFiles} files uploaded
            </Text>
          )}
        </Space>
      </Modal>

      <Title level={3}>
        Upload interviews <Tag color="blue">Beta</Tag>
      </Title>
      <Form
        onFinish={onFinish}
        autoComplete="off"
        layout="vertical"
        form={form}
        requiredMark={false}
      >
        <Paragraph>
          <StyledUploadText>Upload your interview guide</StyledUploadText>
          <ul>
            <StyledListItem>
              Your interview guide CSV should have one question per row in the <i>first column</i>.
            </StyledListItem>
            <StyledListItem>
              Each row should contain the full text of one interview question.
            </StyledListItem>
            <StyledListItem>
              <Link
                href="https://docs.google.com/spreadsheets/d/1h4oyUVIHyJ79hhswV5B6rPqW6TrewD8Lw-iM9P3Ejls/edit?gid=1280319315#gid=1280319315"
                target="_blank"
                rel="noreferrer"
              >
                Interview guide template
              </Link>
            </StyledListItem>
          </ul>
        </Paragraph>

        <Form.Item
          name="interview_guide"
          rules={[{ required: true, message: 'Please attach an interview guide CSV file.' }]}
          valuePropName="fileList"
          getValueFromEvent={(e) => [e.file]}
        >
          <Dragger
            accept=".csv"
            beforeUpload={() => false}
            onChange={(e) => setInterviewGuideFile(e.file)}
            showUploadList={false}
            maxCount={1}
          >
            <StyledInboxOutlined />
            <Space direction="vertical" size={2}>
              {interviewGuideFile ? (
                <>
                  <Space>
                    <Text type="secondary">
                      <PaperClipOutlined />
                    </Text>
                    {interviewGuideFile.name}
                  </Space>
                  <StyledText>Drag or choose a new file</StyledText>
                </>
              ) : (
                <>
                  <Text>Drag interview guide CSV to this area to upload</Text>
                  <StyledText>Or select a CSV file from your computer</StyledText>
                </>
              )}
            </Space>
          </Dragger>
        </Form.Item>

        <Paragraph>
          <StyledUploadText>Upload your transcripts</StyledUploadText>
          <ul>
            <StyledListItem>Multiple files supported</StyledListItem>
            <StyledListItem>Transcripts must identify speakers</StyledListItem>
            <StyledListItem>TXT format supported</StyledListItem>
          </ul>
        </Paragraph>
        <Form.Item
          name="transcripts"
          rules={[{ required: true, message: 'Please attach at least one transcript file.' }]}
          valuePropName="fileList"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) return e;
            return e?.fileList || [];
          }}
        >
          <Upload.Dragger accept=".txt" multiple beforeUpload={() => false}>
            <StyledInboxOutlined />
            <Space direction="vertical" size={2}>
              <Text>Drag transcript files to this area to upload</Text>
              <StyledText>Or select files from your computer</StyledText>
            </Space>
          </Upload.Dragger>
        </Form.Item>

        <Form.Item
          label="Project"
          name="project_name"
          rules={[{ required: true, message: 'Project name is required.' }]}
        >
          <Input placeholder="Name of your interview analysis project" />
        </Form.Item>

        <Form.Item
          label="Research goals"
          name="research_goals"
          tooltip="Adding your goals and key research questions helps Fathom tailor the coding and analysis to your specific needs"
        >
          <TextArea placeholder="Describe your research goals" autoSize={{ minRows: 4 }} />
        </Form.Item>

        <Form.Item
          label="Want to reuse an existing code frame?"
          name="coding_guidance"
          rules={[{ required: true, message: 'Selection is required.' }]}
        >
          <StyledRadioGroup
            options={[
              {
                value: true,
                label: (
                  <Text>
                    <Text strong>Yes - </Text> Apply a code frame from a past project
                  </Text>
                ),
              },
              {
                value: false,
                label: (
                  <Text>
                    <Text strong>No - </Text> Create a new code frame for all questions
                  </Text>
                ),
              },
            ]}
          />
        </Form.Item>

        <Form.Item>
          <Row justify="end">
            <Space>
              <Button onClick={showConfirm}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Continue
              </Button>
            </Space>
          </Row>
        </Form.Item>
      </Form>
    </>
  );
}

FileUploadForm.propTypes = {
  setTranscriptData: PropTypes.func.isRequired,
  transcriptData: transcriptDetailsType.isRequired,
  next: PropTypes.func.isRequired,
  setCodingGuidanceSelected: PropTypes.func.isRequired,
  finishUpload: PropTypes.func.isRequired,
};

export default FileUploadForm;
